import { Power1 } from 'gsap';
import Rectangle from '../../graphics/Rectangle';
import Text from '../../graphics/Text';
import DraggablePreset from './DraggablePreset';
import { NONE } from '../../constants/DropShadow';
import GraphicMask from '../../graphics/GraphicMask';
import {
  getAlignX,
  getCenterY,
  getFixBelowY,
  getFixLeftX,
  getFixRightX,
  getFixTopY,
  getXAccountingForAnchor,
  getYAccountingForAnchor,
} from '../../utils/CoordinateCalculator';

const MAX_DURATION = 2;
const SIDE_LINE_PORTION = 0.5;
const TEXT_SLIDE_PORTION = 0.5;

export default class SideLine extends DraggablePreset {
  constructor(app) {
    super(app);

    this.textMask = new GraphicMask();

    this.topText = new Text(app);
    this.topText.mask = this.textMask.mask;

    this.bottomText = new Text(app);
    this.bottomText.mask = this.textMask.mask;

    this.sideLine = new Rectangle();

    this.container.addChild(this.textMask.mask);

    // Need to add text at first position so that it will draw before the side line
    this.container.addChildAt(this.topText, 0);
    this.container.addChildAt(this.bottomText, 0);
  }

  update(model) {
    model = { ...model, dropShadow: NONE }; // disable drop shadow for this preset
    super.update(model);
    const halfDuration = Math.min(this.duration, MAX_DURATION) / 2;
    const sideLineDuration = halfDuration * SIDE_LINE_PORTION;
    const textSlideDuration = halfDuration * TEXT_SLIDE_PORTION;

    const lineOnLeft = this.align === 'left';

    // top text content and style
    this.topText.text = this.getTextLine(0);
    this.topText.updateStyle(model, {
      fontWeight: 'bold',
    });
    const scale = this.topText.fontSize;

    // bottom text content and style
    this.bottomText.text = this.getTextLine(1);
    this.bottomText.updateStyle(model, {
      scale: 2 / 3,
    });

    this.sideLine.color = this.secondaryColor;
    this.sideLine.width = scale * 0.1;
    this.sideLine.height = 0;

    const verticalMargin = 0.1 * scale;

    this.setDraggableRoot(
      this.topText.innerControls.height +
        this.bottomText.innerControls.height +
        verticalMargin * 3,
      Math.max(
        this.topText.innerControls.width,
        this.bottomText.innerControls.width
      ) +
        this.sideLine.width * 2
    );

    // position off of root
    this.sideLine.x = lineOnLeft
      ? getFixLeftX(this.sideLine, this.root)
      : getFixRightX(this.sideLine, this.root);
    this.sideLine.y = getCenterY(this.sideLine, this.root);

    this.topText.innerControls.y =
      getFixTopY(this.topText.innerControls, this.root) + verticalMargin;
    this.bottomText.innerControls.y =
      getFixBelowY(this.bottomText.innerControls, this.topText.innerControls) +
      verticalMargin;

    this.textMask.width = this.root.width - this.sideLine.width;
    this.textMask.height = this.root.height;
    this.textMask.x = lineOnLeft
      ? this.sideLine.x + this.sideLine.width
      : getFixLeftX(this.textMask, this.root);
    this.textMask.y = getYAccountingForAnchor(this.root);

    // initial values to make sure animation works on startup
    this.topText.innerControls.x = lineOnLeft
      ? getXAccountingForAnchor(this.root) - this.topText.innerControls.width
      : getXAccountingForAnchor(this.root) + this.root.width;
    this.bottomText.innerControls.x = lineOnLeft
      ? getXAccountingForAnchor(this.root) - this.bottomText.innerControls.width
      : getXAccountingForAnchor(this.root) + this.root.width;

    // open side line
    this.animate(this.sideLine)
      .to({
        y: getYAccountingForAnchor(this.root),
        height: this.root.height,
        duration: sideLineDuration,
        ease: Power1.easeOut,
      })
      .reverseOut();

    const targetTextBounds = {
      x: lineOnLeft ? this.textMask.x + this.sideLine.width : this.textMask.x,
      width: this.textMask.width - this.sideLine.width,
    };

    // slide text in
    this.animate(this.topText.innerControls)
      .to(
        {
          x: getAlignX(
            this.topText.innerControls,
            targetTextBounds,
            this.align
          ),
          ease: Power1.easeOut,
          duration: textSlideDuration,
        },
        sideLineDuration
      )
      .reverseOut();

    this.animate(this.bottomText.innerControls)
      .to(
        {
          x: getAlignX(
            this.bottomText.innerControls,
            targetTextBounds,
            this.align
          ),
          ease: Power1.easeOut,
          duration: textSlideDuration,
        },
        sideLineDuration + textSlideDuration / 2
      )
      .reverseOut();
  }

  render() {
    super.render();
    this.sideLine.draw(this.graphics);
    this.textMask.render();
  }
}
